import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../styles/questionnaire.css';
import { saveQuestionnaireResult } from '../../utils/supabase';
import { useAuth } from '../../components/Auth/AuthContext';

const PersonalQuestionnaire = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [currentPage, setCurrentPage] = useState(1);
  const [formData, setFormData] = useState({
    ageRange: '',
    occupation: '',
    skillsToImprove: [],
    preferredScenarios: '',
    communicationStyle: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 处理输入变化
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // 清除该字段的错误
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  // 处理复选框变化
  const handleCheckboxChange = (e) => {
    const { value, checked } = e.target;
    let updatedSkills = [...formData.skillsToImprove];
    
    if (checked) {
      if (updatedSkills.length < 3) {
        updatedSkills.push(value);
      }
    } else {
      updatedSkills = updatedSkills.filter(skill => skill !== value);
    }
    
    setFormData({
      ...formData,
      skillsToImprove: updatedSkills
    });
    
    // 清除该字段的错误
    if (errors.skillsToImprove) {
      setErrors({
        ...errors,
        skillsToImprove: ''
      });
    }
  };

  // 验证当前页面
  const validatePage = () => {
    const newErrors = {};
    
    if (currentPage === 1) {
      if (!formData.ageRange) {
        newErrors.ageRange = 'Please select your age range';
      }
      if (!formData.occupation) {
        newErrors.occupation = 'Please select your occupation';
      }
    } else if (currentPage === 2) {
      if (formData.skillsToImprove.length === 0) {
        newErrors.skillsToImprove = 'Please select at least one skill to improve';
      }
      if (!formData.preferredScenarios) {
        newErrors.preferredScenarios = 'Please select your preferred scenario type';
      }
      if (!formData.communicationStyle) {
        newErrors.communicationStyle = 'Please select your communication style';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 处理下一页
  const handleNext = () => {
    if (validatePage()) {
      window.scrollTo(0, 0);
      setCurrentPage(2);
    }
  };

  // 处理上一页
  const handlePrevious = () => {
    window.scrollTo(0, 0);
    setCurrentPage(1);
  };

  // 处理提交
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validatePage()) {
      setIsSubmitting(true);
      try {
        // 保存数据到 localStorage 以便向后兼容
        localStorage.setItem('personalQuestionnaireData', JSON.stringify(formData));
        
        // 如果用户已登录，保存到 Supabase
        if (currentUser && currentUser.id) {
          await saveQuestionnaireResult(currentUser.id, formData);
          console.log('问卷数据已保存到Supabase');
        }
        
        // 导航到问卷完成页面或调查页面
        navigate('/survey-transition');
      } catch (error) {
        console.error('保存问卷数据时出错:', error);
        // 即使有错误，仍然继续导航
        navigate('/survey-transition');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <div className="questionnaire-container">
      <div className="questionnaire-header">
        <h1>Your Personal EQ Journey</h1>
        <p>Tell us a bit about yourself so we can tailor your emotional intelligence experience</p>
      </div>

      <div className="page-indicator">
        <div className={`indicator ${currentPage >= 1 ? 'active' : ''}`}>1</div>
        <div className="indicator-line"></div>
        <div className={`indicator ${currentPage >= 2 ? 'active' : ''}`}>2</div>
      </div>
      
      <form className="questionnaire-form" onSubmit={currentPage === 2 ? handleSubmit : undefined}>
        {currentPage === 1 ? (
          <div className="form-page">
            <h2>About You</h2>
            
            <div className="form-group">
              <label>What's your age range?</label>
              <div className="options-grid">
                {['18-24', '25-34', '35-44', '45-54', '55+'].map(range => (
                  <div 
                    key={range} 
                    className={`option-card ${formData.ageRange === range ? 'selected' : ''}`}
                    onClick={() => handleInputChange({ target: { name: 'ageRange', value: range } })}
                  >
                    {range}
                  </div>
                ))}
              </div>
              {errors.ageRange && <div className="error-message">{errors.ageRange}</div>}
            </div>
            
            <div className="form-group">
              <label>What's your occupation?</label>
              <div className="options-grid">
                {[
                  { value: 'student', label: 'Student' },
                  { value: 'business', label: 'Business Professional' },
                  { value: 'creative', label: 'Creative Professional' },
                  { value: 'technical', label: 'Technical Role' },
                  { value: 'management', label: 'Management' },
                  { value: 'entrepreneur', label: 'Entrepreneur' }
                ].map(option => (
                  <div 
                    key={option.value} 
                    className={`option-card ${formData.occupation === option.value ? 'selected' : ''}`}
                    onClick={() => handleInputChange({ target: { name: 'occupation', value: option.value } })}
                  >
                    {option.label}
                  </div>
                ))}
              </div>
              {errors.occupation && <div className="error-message">{errors.occupation}</div>}
            </div>
            
            <div className="context-block">
              <h3>Why This Matters</h3>
              <p>Different age groups and professions face unique social challenges. For example, young professionals often need networking skills, while managers benefit from leadership communication techniques. Your answers help us suggest scenarios relevant to your daily life.</p>
            </div>
            
            <div className="button-container">
              <button type="button" className="btn-primary" onClick={handleNext}>Continue</button>
            </div>
          </div>
        ) : (
          <div className="form-page">
            <h2>Your EQ Development Goals</h2>
            
            <div className="form-group">
              <label>Which skills would you like to improve? (Select up to 3)</label>
              <div className="checkbox-grid">
                {[
                  { value: 'active-listening', label: 'Active Listening' },
                  { value: 'conflict-resolution', label: 'Conflict Resolution' },
                  { value: 'public-speaking', label: 'Public Speaking' },
                  { value: 'negotiation', label: 'Negotiation' },
                  { value: 'empathy', label: 'Empathy' },
                  { value: 'leadership', label: 'Leadership Communication' },
                  { value: 'networking', label: 'Networking' },
                  { value: 'assertiveness', label: 'Assertiveness' },
                  { value: 'nonverbal', label: 'Nonverbal Communication' }
                ].map(skill => (
                  <div key={skill.value} className="checkbox-option">
                    <input
                      type="checkbox"
                      id={skill.value}
                      name="skillsToImprove"
                      value={skill.value}
                      checked={formData.skillsToImprove.includes(skill.value)}
                      onChange={handleCheckboxChange}
                      disabled={!formData.skillsToImprove.includes(skill.value) && formData.skillsToImprove.length >= 3}
                    />
                    <label htmlFor={skill.value}>{skill.label}</label>
                  </div>
                ))}
              </div>
              <div className="selection-count">
                Selected: {formData.skillsToImprove.length}/3
              </div>
              {errors.skillsToImprove && <div className="error-message">{errors.skillsToImprove}</div>}
            </div>
            
            <div className="form-group">
              <label>What types of scenarios would be most helpful?</label>
              <div className="options-grid">
                {[
                  { value: 'workplace', label: 'Workplace Interactions' },
                  { value: 'social', label: 'Social Settings' },
                  { value: 'personal', label: 'Personal Relationships' },
                  { value: 'public', label: 'Public Speaking' }
                ].map(option => (
                  <div 
                    key={option.value} 
                    className={`option-card ${formData.preferredScenarios === option.value ? 'selected' : ''}`}
                    onClick={() => handleInputChange({ target: { name: 'preferredScenarios', value: option.value } })}
                  >
                    {option.label}
                  </div>
                ))}
              </div>
              {errors.preferredScenarios && <div className="error-message">{errors.preferredScenarios}</div>}
            </div>
            
            <div className="form-group">
              <label>How would you describe your communication style?</label>
              <div className="options-grid">
                {[
                  { value: 'direct', label: 'Direct & To-The-Point' },
                  { value: 'analytical', label: 'Analytical & Detailed' },
                  { value: 'supportive', label: 'Supportive & Empathetic' },
                  { value: 'expressive', label: 'Expressive & Animated' }
                ].map(option => (
                  <div 
                    key={option.value} 
                    className={`option-card ${formData.communicationStyle === option.value ? 'selected' : ''}`}
                    onClick={() => handleInputChange({ target: { name: 'communicationStyle', value: option.value } })}
                  >
                    {option.label}
                  </div>
                ))}
              </div>
              {errors.communicationStyle && <div className="error-message">{errors.communicationStyle}</div>}
            </div>
            
            <div className="context-block">
              <h3>Real-World Application</h3>
              <p>We'll match your preferences with relevant Western social contexts. For example, if you selected "Workplace Interactions" and "Conflict Resolution," we might present scenarios about handling disagreements with colleagues while maintaining professional relationships.</p>
            </div>
            
            <div className="button-container">
              <button type="button" className="btn-secondary" onClick={handlePrevious}>Back</button>
              <button type="submit" className="btn-primary" disabled={isSubmitting}>Submit</button>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default PersonalQuestionnaire; 